import { CadModelCategory, CadModelCategoryFilterObjectData } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import i18n from "@i18n/i18n";
import { Box, Checkbox, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { DataGridPremium, GridColDef, GridFilterInputValueProps, GridFilterOperator, GridToolbar } from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

export interface ICadModelCategoryFilter extends CadModelCategory {
  isSelected: boolean;
  id: number;
  categoryId: string;
}

const CadModelCategoryFilterGrid = observer(
  ({
    onSelectedRowsChange,
    selectedProfileConfiguration,
    profileId,
    loadProfileConfiguration,
  }: {
    onSelectedRowsChange: (selectedRows: ICadModelCategoryFilter[]) => void;
    selectedProfileConfiguration?: CadModelCategoryFilterObjectData[];
    profileId: string | null;
    loadProfileConfiguration: boolean;
  }) => {
    const containerRef = useRef(null);
    const { height } = useResizeObserver({ ref: containerRef });

    const { data: cadModelCategories, isLoading } = useCadModelCategories();
    const [rows, setRows] = useState<ICadModelCategoryFilter[]>([]);
    const [allRows, setAllRows] = useState<ICadModelCategoryFilter[]>([]);

    const handleCheckboxChange = (cadModelCategoryId: string, checked: boolean) => {
      setRows((prevRows) => prevRows.map((row) => (row.cadModelCategoryId === cadModelCategoryId ? { ...(row as any), isSelected: checked } : row)));
    };

    useEffect(() => {
      console.log("API Data:", cadModelCategories);
      const cadModelCategoryOptions: ICadModelCategoryFilter[] = [];

      if (cadModelCategories) {
        cadModelCategories.forEach((item, index) => {
          cadModelCategoryOptions.push({ ...(item as any), id: index, isSelected: false, categoryId: item.cadModelCategoryId });
        });
      }

      setRows(cadModelCategoryOptions);
    }, [cadModelCategories]);

    useEffect(() => {
      const selectedRows = rows.filter((row) => row.isSelected);
      onSelectedRowsChange(selectedRows);
    }, [rows, onSelectedRowsChange]);

    useEffect(() => {
      if (rows && rows.length > 0) {
        const _rows = [...rows];
        _rows.forEach((x) => (x.isSelected = false));

        if (selectedProfileConfiguration && selectedProfileConfiguration.length > 0) {
          selectedProfileConfiguration.forEach((config) => {
            const index = _rows.findIndex((x) => x.cadModelCategoryId === config.categoryId);
            if (index !== -1) {
              _rows[index].isSelected = true;
            }
          });
        }

        setRows([..._rows]);
        setAllRows([..._rows]);
      }
    }, [selectedProfileConfiguration]);

    const SelectFilterInput = ({ item, applyValue }: GridFilterInputValueProps) => {
      const handleChange = (event: SelectChangeEvent<any>) => {
        applyValue({ ...item, value: event.target.value });
      };

      return (
        <Box sx={{ display: "flex", alignItems: "center", height: 65, pl: "20px" }}>
          <Select value={item.value || ""} onChange={handleChange} displayEmpty>
            <MenuItem value="">{i18n.t("cad-model-category-filter.page.filter-all")}</MenuItem>
            <MenuItem value="checked">{i18n.t("cad-model-category-filter.page.filter-checked")}</MenuItem>
            <MenuItem value="unchecked">{i18n.t("cad-model-category-filter.page.filter-unchecked")}</MenuItem>
          </Select>
        </Box>
      );
    };

    const selectFilterOperators: GridFilterOperator<any, string>[] = [
      {
        label: "Checked/Unchecked",
        value: "filter",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.value) return null; // Show all if no value is selected
          return (value) => (filterItem.value === "checked" ? Boolean(value) : !Boolean(value));
        },
        InputComponent: SelectFilterInput,
      },
    ];

    const columns: GridColDef[] = [
      {
        field: "name",
        headerName: i18n.t("cad-model-category-filter.grid.columns.cad-category"),
        editable: true,
        flex: 1,
      },
      {
        field: "isSelected",
        headerName: i18n.t("cad-model-category-filter.grid.columns.enable"),
        editable: true,
        flex: 1,
        filterOperators: selectFilterOperators,
        renderCell: (params) => <Checkbox checked={params.value} onChange={(e) => handleCheckboxChange(params.row.cadModelCategoryId, e.target.checked)} />,
      },
    ];

    const NoRowsOverlay = () => {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
          {i18n.t("cad-model-category-filter.grid.rows.no-data-message")}
        </Box>
      );
    };

    const getSortedRowIds = () => {
      return allRows
        .sort((a, b) => {
          const nameA = a.name || "";
          const nameB = b.name || "";
          return nameA.localeCompare(nameB);
        })
        .map((row) => row.id);
    };

    return (
      <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
        <DataGridPremium
          loading={isLoading || loadProfileConfiguration}
          rows={profileId ? rows : []}
          columns={columns}
          pagination
          disableColumnMenu
          initialState={{
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          sx={{ height: `${(height || 400) - 16}px` }}
          slots={{ toolbar: GridToolbar, noRowsOverlay: NoRowsOverlay }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              csvOptions: {
                allColumns: true,
                getRowsToExport: getSortedRowIds,
              },
              excelOptions: {
                allColumns: true,
                getRowsToExport: getSortedRowIds,
              },
            },
          }}
        />
      </Box>
    );
  },
);

export default CadModelCategoryFilterGrid;
